import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, Sparkles, Trash2, ChevronDown, ChevronUp, Check, AlertTriangle, Layers, UserPlus, Film } from 'lucide-react';
import PresentationBuilder from './PresentationBuilder';

const API = process.env.REACT_APP_BACKEND_URL;

const SCENARIO_ICONS = {
  fully_booked_birthday: '🎂',
  fully_booked_wedding: '💍',
  fully_booked_corporate: '🏢',
  payment_due: '💳',
  proposal_received: '📋',
  planning_empty: '📝',
  almost_complete: '⏳',
  booked_upcoming_one_action: '🎯',
  payment_past_due: '🔴',
  vendor_no_response: '🔕',
  multiple_proposals_received: '📨',
  booking_cancelled: '❌',
  event_completed: '✅',
  vendor_message_unread: '💬',
  everything_broken: '🔥',
  hero_card_showcase: '🎨',
  mixed_real_world_feed: '🌐',
  high_activity_events: '🚀',
  minimal_vs_decorated: '⚖️',
  duplicate_type_stress_test: '🔁',
};

const AdminScenariosTab = () => {
  const [users, setUsers] = useState([]);
  const [scenarios, setScenarios] = useState({});
  const [batches, setBatches] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedScenario, setSelectedScenario] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [vendorSetupOpen, setVendorSetupOpen] = useState(false);
  const [vendorSetupEnabled, setVendorSetupEnabled] = useState(false);
  const [vendorConfig, setVendorConfig] = useState({
    category: 'Photography',
    zip_code: '90001',
    service_radius: 25,
    stripe_connected: false,
    profile_completion: 50,
    has_photos: false,
    service_type: 'none',
    total_requests: 0,
    needs_response: 0,
    open_requests: 0,
    payments_to_review: 0,
  });
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [vendorResult, setVendorResult] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, scenariosRes, batchesRes] = await Promise.all([
        fetch(`${API}/api/admin/users`, { headers }),
        fetch(`${API}/api/admin/scenarios/list`, { headers }),
        fetch(`${API}/api/admin/scenarios/batches`, { headers }),
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        const customers = (data.users || data || []).filter(u =>
          (u.roles || [u.role]).some(r => ['customer', 'admin'].includes(r))
        );
        setUsers(customers);
      }
      if (scenariosRes.ok) setScenarios(await scenariosRes.json());
      if (batchesRes.ok) setBatches(await batchesRes.json());
      else if (scenariosRes.status === 403) setError('Scenario generator is disabled. Set ENABLE_ADMIN_SCENARIOS=true in backend .env');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generate = async () => {
    if (!selectedUser || !selectedScenario) return;
    setGenerating(true);
    setResult(null);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/generate-scenario`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: selectedUser, scenario: selectedScenario }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      setResult(data);
      // Refresh batches
      const bRes = await fetch(`${API}/api/admin/scenarios/batches`, { headers });
      if (bRes.ok) setBatches(await bRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Delete this scenario batch and all its data?')) return;
    setDeletingBatch(batchId);
    try {
      const res = await fetch(`${API}/api/admin/scenarios/batch/${batchId}`, { method: 'DELETE', headers });
      if (res.ok) setBatches(prev => prev.filter(b => b.batch_id !== batchId));
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingBatch(null);
    }
  };

  const createTestUser = async () => {
    if (!newUserName.trim()) return;
    setCreatingUser(true);
    setError(null);
    setCreatedUser(null);
    try {
      const res = await fetch(`${API}/api/admin/scenarios/create-test-user`, {
        method: 'POST', headers,
        body: JSON.stringify({
          name: newUserName.trim(),
          email: newUserEmail.trim() || undefined,
          password: newUserPassword.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create user');
      setCreatedUser(data);
      setSelectedUser(data.id);
      const usersRes = await fetch(`${API}/api/admin/users`, { headers });
      if (usersRes.ok) {
        const uData = await usersRes.json();
        const customers = (uData.users || uData || []).filter(u =>
          (u.roles || [u.role]).some(r => ['customer', 'admin'].includes(r))
        );
        setUsers(customers);
      }
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
    } catch (e) {
      setError(e.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const createVendor = async (userId) => {
    if (!userId) return;
    setCreatingVendor(true);
    setVendorResult(null);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/scenarios/create-vendor-for-user`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: userId, ...vendorConfig }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create vendor');
      setVendorResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreatingVendor(false);
    }
  };

  const generateBulk = async (presetSet) => {
    if (!selectedUser) return;
    setBulkGenerating(presetSet);
    setBulkResults(null);
    setResult(null);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/scenarios/generate-bulk`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: selectedUser, preset_set: presetSet }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk generation failed');
      setBulkResults(data);
      const bRes = await fetch(`${API}/api/admin/scenarios/batches`, { headers });
      if (bRes.ok) setBatches(await bRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkGenerating(false);
    }
  };

  const [activeTab, setActiveTab] = useState('presentation');

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-scenarios-tab">
      {/* Tab Switcher */}
      <div className="flex border-b border-gray-200 gap-1" data-testid="scenario-tabs">
        <button
          onClick={() => setActiveTab('presentation')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'presentation'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
          data-testid="tab-presentation"
        >
          <Film className="h-4 w-4" />
          Presentation Builder
        </button>
        <button
          onClick={() => setActiveTab('scenarios')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'scenarios'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
          data-testid="tab-scenarios"
        >
          <Sparkles className="h-4 w-4" />
          Scenario Generator
        </button>
      </div>

      {/* Presentation Builder Tab */}
      {activeTab === 'presentation' && <PresentationBuilder />}

      {/* Scenario Generator Tab */}
      {activeTab === 'scenarios' && (
        <>
      {/* Generator */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900">Scenario Generator</h3>
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700" data-testid="scenario-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Quick Create Test User */}
          <div className="md:col-span-2 border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50/50">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Quick Create Test User</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <input
                type="text"
                placeholder="Name (required)"
                value={newUserName}
                onChange={e => setNewUserName(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 outline-none"
                data-testid="new-user-name-input"
              />
              <input
                type="email"
                placeholder="Email (auto-generated)"
                value={newUserEmail}
                onChange={e => setNewUserEmail(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 outline-none"
                data-testid="new-user-email-input"
              />
              <input
                type="text"
                placeholder="Password (auto-generated)"
                value={newUserPassword}
                onChange={e => setNewUserPassword(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 outline-none"
                data-testid="new-user-password-input"
              />
            </div>
            <Button
              onClick={createTestUser}
              disabled={!newUserName.trim() || creatingUser}
              className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="create-test-user-btn"
            >
              {creatingUser ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Creating...</> : 'Create & Use User'}
            </Button>
            {createdUser && (
              <div className="mt-2 text-xs text-green-700 bg-green-50 rounded px-3 py-2" data-testid="created-user-info">
                Created: <strong>{createdUser.name}</strong> ({createdUser.email}) — Password: <code className="bg-green-100 px-1 rounded">{createdUser.password}</code>
              </div>
            )}

            {/* ── Vendor Setup (Optional) ── */}
            <div className="mt-4 border-t border-gray-200 pt-3" data-testid="vendor-setup-section">
              <button
                type="button"
                onClick={() => setVendorSetupOpen(!vendorSetupOpen)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-blue-500" />
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Vendor Setup (Optional)</span>
                </div>
                {vendorSetupOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </button>

              {vendorSetupOpen && (
                <div className="mt-3 space-y-4">
                  {/* Enable toggle */}
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="vendor-setup-toggle">
                    <input
                      type="checkbox"
                      checked={vendorSetupEnabled}
                      onChange={e => setVendorSetupEnabled(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Create vendor for this user</span>
                  </label>

                  {vendorSetupEnabled && (
                    <div className="space-y-4 bg-blue-50/50 rounded-lg p-3 border border-blue-100">
                      {/* Basic */}
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Basic</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <select
                            value={vendorConfig.category}
                            onChange={e => setVendorConfig(p => ({ ...p, category: e.target.value }))}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none"
                            data-testid="vendor-category-select"
                          >
                            {['Photography', 'DJ/Music', 'Florist', 'Catering', 'Decor/Design', 'Bakery/Cakes', 'Audio/Visual', 'Videography'].map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="ZIP Code"
                            value={vendorConfig.zip_code}
                            onChange={e => setVendorConfig(p => ({ ...p, zip_code: e.target.value }))}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none"
                            data-testid="vendor-zip-input"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={vendorConfig.service_radius}
                              onChange={e => setVendorConfig(p => ({ ...p, service_radius: parseInt(e.target.value) || 0 }))}
                              className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none"
                              data-testid="vendor-radius-input"
                            />
                            <span className="text-xs text-gray-500">mi radius</span>
                          </div>
                        </div>
                      </div>

                      {/* Setup State */}
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Setup State</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <label className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-200">
                            <input
                              type="checkbox"
                              checked={vendorConfig.stripe_connected}
                              onChange={e => setVendorConfig(p => ({ ...p, stripe_connected: e.target.checked }))}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                              data-testid="vendor-stripe-toggle"
                            />
                            <span className="text-xs text-gray-700">Stripe connected</span>
                          </label>
                          <label className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-200">
                            <input
                              type="checkbox"
                              checked={vendorConfig.has_photos}
                              onChange={e => setVendorConfig(p => ({ ...p, has_photos: e.target.checked }))}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                              data-testid="vendor-photos-toggle"
                            />
                            <span className="text-xs text-gray-700">Has photos</span>
                          </label>
                          <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-200">
                            <span className="text-xs text-gray-700 shrink-0">Profile:</span>
                            <input
                              type="range"
                              min="0" max="100" step="5"
                              value={vendorConfig.profile_completion}
                              onChange={e => setVendorConfig(p => ({ ...p, profile_completion: parseInt(e.target.value) }))}
                              className="flex-1 h-1.5 accent-blue-600"
                              data-testid="vendor-completion-slider"
                            />
                            <span className="text-xs font-mono text-blue-600 w-8 text-right">{vendorConfig.profile_completion}%</span>
                          </div>
                          <select
                            value={vendorConfig.service_type}
                            onChange={e => setVendorConfig(p => ({ ...p, service_type: e.target.value }))}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none"
                            data-testid="vendor-service-type-select"
                          >
                            <option value="none">No services</option>
                            <option value="simple">Simple services</option>
                            <option value="packages">Packages</option>
                            <option value="both">Both (packages + services)</option>
                          </select>
                        </div>
                      </div>

                      {/* Requests Simulation */}
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Requests Simulation</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { key: 'total_requests', label: 'Total requests' },
                            { key: 'needs_response', label: 'Needs response' },
                            { key: 'open_requests', label: 'Open requests' },
                          ].map(f => (
                            <div key={f.key} className="bg-white rounded-lg px-2.5 py-2 border border-gray-200">
                              <input
                                type="number"
                                min="0" max="100"
                                value={vendorConfig[f.key]}
                                onChange={e => setVendorConfig(p => ({ ...p, [f.key]: parseInt(e.target.value) || 0 }))}
                                className="w-full text-sm font-medium text-gray-900 outline-none mb-0.5"
                                data-testid={`vendor-${f.key.replace(/_/g, '-')}`}
                              />
                              <p className="text-[10px] text-gray-500">{f.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Payments Simulation */}
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Payments Simulation</p>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-white rounded-lg px-2.5 py-2 border border-gray-200">
                            <input
                              type="number"
                              min="0" max="50"
                              value={vendorConfig.payments_to_review}
                              onChange={e => setVendorConfig(p => ({ ...p, payments_to_review: parseInt(e.target.value) || 0 }))}
                              className="w-full text-sm font-medium text-gray-900 outline-none mb-0.5"
                              data-testid="vendor-payments-to-review"
                            />
                            <p className="text-[10px] text-gray-500">Payments to review</p>
                          </div>
                        </div>
                      </div>

                      {/* Create Vendor Button */}
                      <Button
                        onClick={() => createVendor(createdUser?.id || selectedUser)}
                        disabled={(!createdUser?.id && !selectedUser) || creatingVendor}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                        data-testid="create-vendor-btn"
                      >
                        {creatingVendor ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Creating Vendor...</> : 'Create Vendor Profile'}
                      </Button>

                      {/* Vendor Result */}
                      {vendorResult && (
                        <div className="text-xs text-blue-700 bg-blue-50 rounded px-3 py-2 border border-blue-200" data-testid="vendor-result">
                          Vendor created: <strong>{vendorResult.vendor_name}</strong>
                          <span className="text-blue-500"> — Profile: {vendorResult.profile_completion}%</span>
                          <span className="text-blue-500"> | Stripe: {vendorResult.stripe_connected ? 'connected' : 'not connected'}</span>
                          <span className="text-blue-500"> | Services: {vendorResult.service_type}</span>
                          {vendorResult.activity && (vendorResult.activity.total > 0 || vendorResult.activity.payments > 0) && (
                            <span className="text-blue-500"> | Requests: {vendorResult.activity.total}, Payments: {vendorResult.activity.payments}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* User selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target User</label>
            <div className="relative">
              <select
                value={selectedUser}
                onChange={e => setSelectedUser(e.target.value)}
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm bg-white focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 outline-none"
                data-testid="scenario-user-select"
              >
                <option value="">Select a user...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Scenario selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scenario Preset</label>
            <div className="relative">
              <select
                value={selectedScenario}
                onChange={e => setSelectedScenario(e.target.value)}
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm bg-white focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 outline-none"
                data-testid="scenario-preset-select"
              >
                <option value="">Select a scenario...</option>
                {(() => {
                  const entries = Object.entries(scenarios);
                  const singles = entries.filter(([, s]) => s.type !== 'multi');
                  const multis = entries.filter(([, s]) => s.type === 'multi');
                  return (
                    <>
                      {singles.length > 0 && <optgroup label="Single Event Scenarios">
                        {singles.map(([key, s]) => (
                          <option key={key} value={key}>{SCENARIO_ICONS[key] || '📌'} {s.label}</option>
                        ))}
                      </optgroup>}
                      {multis.length > 0 && <optgroup label="Multi-Event Visual Tests">
                        {multis.map(([key, s]) => (
                          <option key={key} value={key}>{SCENARIO_ICONS[key] || '🎨'} {s.label} ({s.events_count} events)</option>
                        ))}
                      </optgroup>}
                    </>
                  );
                })()}
              </select>
              <ChevronDown className="absolute right-2.5 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <Button
          onClick={generate}
          disabled={!selectedUser || !selectedScenario || generating}
          className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 rounded-lg font-medium disabled:opacity-50"
          data-testid="generate-scenario-btn"
        >
          {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating...</> : 'Generate Scenario'}
        </Button>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-testid="scenario-result">
          <div className="flex items-center gap-2 mb-3">
            <Check className="h-5 w-5 text-green-600" />
            <h4 className="font-semibold text-green-800">
              {result.events_generated ? `${result.events_generated} Events Generated` : 'Scenario Generated'}
            </h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-green-600">Scenario:</span> <span className="font-medium">{result.scenario_label}</span></div>
            <div><span className="text-green-600">User:</span> <span className="font-medium">{result.user_name}</span></div>
            {result.event_id && <div><span className="text-green-600">Event ID:</span> <span className="font-mono text-xs">{result.event_id?.slice(0, 8)}...</span></div>}
            <div><span className="text-green-600">Batch:</span> <span className="font-mono text-xs">{result.batch_id}</span></div>
          </div>
          {result.events && result.events.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {result.events.map((evt, i) => (
                <div key={i} className="flex items-center gap-2 bg-green-100/60 rounded-lg px-3 py-1.5 text-xs">
                  <span className="font-medium text-green-800 min-w-[130px]">{evt.event_type}</span>
                  <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{evt.flow?.replace(/_/g, ' ')}</span>
                  <span className="text-green-500 font-mono ml-auto">{evt.event_id?.slice(0, 8)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-3 text-xs">
            {Object.entries(result.summary || {}).map(([k, v]) => (
              v > 0 && <span key={k} className="bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">{k}: {v}</span>
            ))}
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="bulk-actions-section">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="h-5 w-5 text-indigo-500" />
          <h3 className="text-lg font-semibold text-gray-900">Bulk Generation</h3>
        </div>
        {!selectedUser ? (
          <p className="text-sm text-gray-500">Select a Target User above to enable bulk generation.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => generateBulk('core')}
              disabled={!!bulkGenerating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-50"
              data-testid="generate-core-presets-btn"
            >
              {bulkGenerating === 'core' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating Core...</> : 'Generate Core Presets (7)'}
            </Button>
            <Button
              onClick={() => generateBulk('all')}
              disabled={!!bulkGenerating}
              className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-50"
              data-testid="generate-all-presets-btn"
            >
              {bulkGenerating === 'all' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating All...</> : `Generate All Single Presets (${Object.entries(scenarios).filter(([,s]) => s.type !== 'multi').length})`}
            </Button>
            <Button
              onClick={() => generateBulk('visual')}
              disabled={!!bulkGenerating}
              className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-50"
              data-testid="generate-visual-presets-btn"
            >
              {bulkGenerating === 'visual' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating Visual...</> : `Generate Visual Presets (${Object.entries(scenarios).filter(([,s]) => s.type === 'multi').length})`}
            </Button>
            <Button
              onClick={() => generateBulk('everything')}
              disabled={!!bulkGenerating}
              className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-50"
              data-testid="generate-everything-btn"
            >
              {bulkGenerating === 'everything' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating Everything...</> : `Generate Everything (${Object.keys(scenarios).length})`}
            </Button>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">Existing presets for the selected user are skipped automatically. Each preset creates a separate batch.</p>
      </div>

      {/* Bulk Results */}
      {bulkResults && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5" data-testid="bulk-results">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-indigo-600" />
              <h4 className="font-semibold text-indigo-800">Bulk Generation Complete</h4>
            </div>
            <div className="flex gap-2 text-xs">
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">{bulkResults.created} created</span>
              {bulkResults.skipped > 0 && <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">{bulkResults.skipped} skipped</span>}
              {bulkResults.errors > 0 && <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">{bulkResults.errors} errors</span>}
            </div>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {bulkResults.results.map((r, i) => (
              <div key={i} className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm ${
                r.status === 'created' ? 'bg-green-50 border border-green-100' :
                r.status === 'skipped' ? 'bg-yellow-50 border border-yellow-100' :
                'bg-red-50 border border-red-100'
              }`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base">{SCENARIO_ICONS[r.scenario] || '📌'}</span>
                  <span className="font-medium truncate">{r.scenario_label}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.status === 'created' && (
                    <>
                      <span className="text-xs text-gray-500 font-mono hidden sm:inline">{r.batch_id?.slice(0, 16)}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Created</span>
                    </>
                  )}
                  {r.status === 'skipped' && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Skipped</span>}
                  {r.status === 'error' && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full" title={r.reason}>Error</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Batch history */}
      {batches.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Generated Batches ({batches.length})</h3>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {batches.map(b => (
              <div key={b.batch_id} className="flex items-center justify-between py-2.5 px-3 border border-gray-100 rounded-lg hover:bg-gray-50 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-base">{SCENARIO_ICONS[b.scenario_type] || '📌'}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{b.scenario_type?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-500">{b.user_name} &middot; {new Date(b.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-400 font-mono">{b.batch_id?.slice(0, 16)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteBatch(b.batch_id)}
                    disabled={deletingBatch === b.batch_id}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    data-testid={`delete-batch-${b.batch_id}`}
                  >
                    {deletingBatch === b.batch_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminScenariosTab;
